import React, { useEffect, useMemo, useState } from 'react';
import Section from './Section';
import SleepChart from './SleepChart';
import SleepSessionCard from './SleepSessionCard';
import { sleepService, SleepSession } from '../services/sleepService';

interface SleepPanelProps {
  patientUserId: number | string;
}

type RangePreset = '7d' | '30d' | '90d';

const RANGE_DAYS: Record<RangePreset, number> = { '7d': 7, '30d': 30, '90d': 90 };

const formatDate = (date: Date): string => date.toISOString().split('T')[0];

const getRangeDates = (preset: RangePreset) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - RANGE_DAYS[preset]);
  return { start: formatDate(start), end: formatDate(end) };
};

const Kpi: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    style={{
      flex: 1,
      minWidth: 120,
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: '12px 14px',
    }}
  >
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937' }}>{value}</div>
  </div>
);

const SleepPanel: React.FC<SleepPanelProps> = ({ patientUserId }) => {
  const [range, setRange] = useState<RangePreset>('30d');
  const [sessions, setSessions] = useState<SleepSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  useEffect(() => {
    const fetchSessions = async () => {
      if (!patientUserId) return;
      try {
        setLoading(true);
        setError(null);
        const { start, end } = getRangeDates(range);
        const response = await sleepService.getSleepSessions(patientUserId, {
          start_date: start,
          end_date: end,
        });
        setSessions(response.results || []);
        setSelectedDate(null);
      } catch (err) {
        console.error('Error fetching sleep sessions:', err);
        setError('Error al cargar los datos de sueño');
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, [patientUserId, range]);

  const kpis = useMemo(() => {
    const main = sessions.filter((s) => s.kind === 'MAIN');
    if (main.length === 0) {
      return { avgDuration: '-', avgEfficiency: '-', avgLatency: '-', avgWaso: '-', napCount: 0 };
    }
    const durations = main.map(
      (s) => (new Date(s.end_time).getTime() - new Date(s.start_time).getTime()) / (1000 * 60 * 60)
    );
    const efficiencies = main.map((s) => s.efficiency).filter((v): v is number => v !== null);
    const latencies = main.map((s) => s.latency_minutes).filter((v): v is number => v !== null);
    const wasos = main.map((s) => s.waso_minutes).filter((v): v is number => v !== null);
    const avg = (arr: number[]) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

    const dur = avg(durations);
    const eff = avg(efficiencies);
    const lat = avg(latencies);
    const wa = avg(wasos);

    const fmtHours = (h: number) => {
      const hh = Math.floor(h);
      const mm = Math.round((h - hh) * 60);
      return `${hh}h ${mm}m`;
    };

    return {
      avgDuration: dur !== null ? fmtHours(dur) : '-',
      avgEfficiency: eff !== null ? `${eff.toFixed(1)}%` : '-',
      avgLatency: lat !== null ? `${Math.round(lat)} min` : '-',
      avgWaso: wa !== null ? `${Math.round(wa)} min` : '-',
      napCount: sessions.filter((s) => s.kind === 'NAP').length,
    };
  }, [sessions]);

  const sessionsForSelectedDay = useMemo(() => {
    if (!sessions.length) return [];
    let date = selectedDate;
    if (!date) {
      date = [...sessions]
        .sort((a, b) => b.session_date.localeCompare(a.session_date))[0]?.session_date;
    }
    if (!date) return [];
    return sessions
      .filter((s) => s.session_date === date)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [sessions, selectedDate]);

  const effectiveSelectedDate = selectedDate
    ?? sessionsForSelectedDay[0]?.session_date
    ?? null;

  return (
    <>
      <Section style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div style={{ fontWeight: 700, color: '#222', fontSize: 16 }}>Progreso de sueño</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['7d', '30d', '90d'] as RangePreset[]).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                style={{
                  background: range === r ? '#38b2ac' : '#fff',
                  color: range === r ? '#fff' : '#374151',
                  border: '1px solid ' + (range === r ? '#38b2ac' : '#d1d5db'),
                  borderRadius: 6,
                  padding: '6px 14px',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {r === '7d' ? '7 días' : r === '30d' ? '30 días' : '90 días'}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <Kpi label="Duración media" value={kpis.avgDuration} />
          <Kpi label="Eficiencia media" value={kpis.avgEfficiency} />
          <Kpi label="Latencia media" value={kpis.avgLatency} />
          <Kpi label="WASO medio" value={kpis.avgWaso} />
          <Kpi label="Siestas" value={String(kpis.napCount)} />
        </div>

        <SleepChart
          sessions={sessions}
          loading={loading}
          error={error}
          selectedDate={effectiveSelectedDate}
          onSelectDate={setSelectedDate}
        />
      </Section>

      <Section style={{ marginBottom: 0 }}>
        <div style={{ fontWeight: 700, color: '#222', fontSize: 16, marginBottom: 12 }}>
          Sesiones {effectiveSelectedDate ? `· ${new Date(effectiveSelectedDate + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}` : ''}
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Cargando sesiones...</div>
        ) : sessionsForSelectedDay.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
            No hay sesiones para mostrar. Selecciona un día en el gráfico.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sessionsForSelectedDay.map((s) => (
              <SleepSessionCard key={s.id} session={s} />
            ))}
          </div>
        )}
      </Section>
    </>
  );
};

export default SleepPanel;
